import he from 'he';
import MediaPlayer from './MediaPlayer';
import type { ChatMessage, ChatAttachment } from '../../types/resource';

// Single source of truth for bubble geometry: attachments are pinned to explicit pixel
// heights here AND summed by ConversationGrid.getRowHeight, so the ag-Grid row height always
// matches the rendered content (mirrors Angular, which fixed img/audio/video heights and used
// matching getRowHeight constants). Keep these in sync with the `.iio-chat-*` rules in _iio.scss.
export const CHAT_LAYOUT = {
  rowPadV: 64, // .iio-chat-row top+bottom padding (16 top + 48 bottom)
  rowPadH: 28, // .iio-chat-row left+right padding (14*2)
  metaH: 20, // sender/timestamp line
  missingH: 40, // "Missing Content" bubble when no text and no attachments
  attachGap: 6, // .iio-chat-attach margin-top
  imageH: 250,
  audioH: 54,
  videoH: 250,
  fileH: 96,
  // Text-bubble measurement (must match the .iio-chat-bubble CSS).
  bubblePadH: 20,
  fontSize: 14,
  lineHeight: 1.4,
  maxWidthPct: 0.7,
} as const;

interface Props {
  // Undefined while ag-Grid's infinite model renders a not-yet-loaded placeholder row.
  data?: ChatMessage;
  // Opens the message's own resource in the normal viewer (Angular
  // showResourceForSelectedMessage). Revealed only on the selected row (CSS).
  onViewResource?: (m: ChatMessage) => void;
}

// A message is "from me" (the phone/account owner) when it has no sender, the sender is the
// phone owner, or the sender id matches the owner's phone/email. Ported verbatim from
// Angular's messageCellRenderer.
export function isFromMe(m: ChatMessage): boolean {
  const from = m.from;
  return !from || from.phoneOwner || from.id === m.ownerPhoneNumber || from.id === m.ownerEmail;
}

const isAudio = (a: ChatAttachment) => !!a.contentType?.includes('audio');
const isVideo = (a: ChatAttachment) => !!a.contentType?.includes('video');

function Attachment({ attachment: a }: { attachment: ChatAttachment }) {
  const playable = !a.infected && (isAudio(a) || isVideo(a));
  const image = !a.infected && a.imageType;

  // Pin each attachment to the explicit height getRowHeight budgets for it.
  const mediaH = isAudio(a) ? CHAT_LAYOUT.audioH : CHAT_LAYOUT.videoH;

  return (
    <div className="iio-chat-attach" data-testid="viewer-message-attachment-item">
      {image && (
        <img src={a.path} alt={a.fileName} style={{ height: CHAT_LAYOUT.imageH }} />
      )}
      {playable && (
        <div className="iio-chat-media" style={{ height: mediaH, width: isAudio(a) ? 320 : 450, maxWidth: '100%' }}>
          <MediaPlayer src={a.path} mimeType={a.contentType} />
        </div>
      )}
      {!image && !playable && (
        // Infected, or a non-media file — show a file icon + name.
        <div className="iio-chat-file" style={{ height: CHAT_LAYOUT.fileH }}>
          <i className="far fa-file fa-4x" />
          <div className="iio-chat-file-name">{a.fileName}</div>
        </div>
      )}
    </div>
  );
}

/**
 * Chat-bubble renderer for a single conversation message (ag-Grid cellRendererFramework).
 * Flexbox layout (see `.iio-chat-row` in _iio.scss) — the owner's messages align right and
 * blue, the other party left and grey. No floats, so the row measures its natural height and
 * ag-Grid's column `autoHeight` sizes each row to fit.
 */
export default function MessageBubble({ data, onViewResource }: Props) {
  // Placeholder row (block still loading). Return an empty element rather than null so
  // ag-grid-react's waitForInstance sees a created component (avoids the "React Component
  // not created within 1000ms" warning).
  if (!data) return <span aria-hidden="true" />;

  const fromMe = isFromMe(data);
  const text = data.text ? he.decode(data.text).trim() : '';
  const attachments = data.attachments ?? [];

  const header = fromMe
    ? [data.from?.name, data.from?.id || data.ownerPhoneNumber].filter(Boolean).join(' ')
    : [data.from?.name, data.from?.id].filter(Boolean).join(' ');

  return (
    <div className={`iio-chat-row ${fromMe ? 'me' : 'them'}`} data-testid="viewer-message-item">
      <div className="iio-chat-meta">
        {header}
        {header ? ' - ' : ''}
        {data.formattedDateTimeSent}
      </div>

      {data.restricted ? (
        <div className="iio-chat-bubble restricted" data-testid="viewer-message-bubble-item">
          <i className="fas fa-exclamation-triangle" /> You do not have permission to view this chat
        </div>
      ) : (
        <>
          {!text && attachments.length === 0 && (
            <div className="iio-chat-bubble" data-testid="viewer-message-bubble-item">
              <i className="fas fa-exclamation-triangle" /> Missing Content
            </div>
          )}
          {text && <div className="iio-chat-bubble" data-testid="viewer-message-bubble-item">{text}</div>}
          {attachments.map((a, i) => (
            <Attachment key={i} attachment={a} />
          ))}
        </>
      )}

      {!data.restricted && onViewResource && (
        // Hidden until the row is selected (see .iio-chat-viewresource CSS), matching
        // Angular's `.ag-row-selected .viewResource { visibility: visible }`.
        <div className="iio-chat-viewresource">
          <button type="button" className="btn btn-primary" onClick={() => onViewResource(data)} data-testid="viewer-message-view-resource-btn">
            View Resource
          </button>
        </div>
      )}

      {data.status === 'Failed' && (
        <i title="Not Delivered" className="fas fa-exclamation-circle iio-chat-failed" />
      )}
    </div>
  );
}
